import type {
  KeenSliderHooks,
  KeenSliderOptions,
  KeenSliderPlugin,
} from "keen-slider";
import type { CarouselProps } from "../carousel/interface";
import { BREAKPOINTS } from "../../../src/themes/interface";

export function generateArrayFromNumber(num: number): number[] {
  return Array(num)
    .fill(0)
    .map((_, i) => i + 1);
}

export function carouselConfig(
  args: Required<
    Pick<
      CarouselProps,
      "transitionSpeed" | "itemsPerSlide" | "itemGap" | "loop"
    >
  >,
): KeenSliderOptions {
  return {
    loop: args.loop,
    defaultAnimation: {
      duration: args.transitionSpeed,
    },
    slides: {
      perView: args.itemsPerSlide.initial || 1.2,
      spacing: args.itemGap.initial || 10,
    },
    breakpoints: {
      [`(min-width: ${BREAKPOINTS.sm})`]: {
        slides: {
          perView: args.itemsPerSlide.sm || 1.2,
          spacing: args.itemGap.sm || 12,
        },
      },
      [`(min-width: ${BREAKPOINTS.md})`]: {
        slides: {
          perView: args.itemsPerSlide.md || 2,
          spacing: args.itemGap.md || 16,
        },
      },
      [`(min-width: ${BREAKPOINTS.lg})`]: {
        slides: {
          perView: args.itemsPerSlide.lg || 3,
          spacing: args.itemGap.lg || 20,
        },
      },
      [`(min-width: ${BREAKPOINTS.xl})`]: {
        slides: {
          perView: args.itemsPerSlide.xl || 4,
          spacing: args.itemGap.xl || 24,
        },
      },
      [`(min-width: ${BREAKPOINTS["2xl"]})`]: {
        slides: {
          perView: args.itemsPerSlide["2xl"] || 4,
          spacing: args.itemGap["2xl"] || 24,
        },
      },
    },
  };
}

export const CarouselAutoPlayPlugin = (
  interval?: number,
  pauseOnHover?: boolean,
): KeenSliderPlugin<unknown, unknown, KeenSliderHooks> => {
  return (slider) => {
    let timeout: ReturnType<typeof setTimeout>;
    let mouseOver = false;

    function clearNextTimeout() {
      clearTimeout(timeout);
    }

    function nextTimeout() {
      clearTimeout(timeout);
      if (mouseOver) return;
      timeout = setTimeout(() => {
        slider.next();
      }, interval || 2000);
    }

    slider.on("created", () => {
      if (pauseOnHover) {
        slider.container.addEventListener("mouseover", () => {
          mouseOver = true;
          clearNextTimeout();
        });
        slider.container.addEventListener("mouseout", () => {
          mouseOver = false;
          nextTimeout();
        });
      }
      nextTimeout();
    });

    slider.on("dragStarted", clearNextTimeout);
    slider.on("animationEnded", nextTimeout);
    slider.on("updated", nextTimeout);

    return () => {
      clearNextTimeout();
    };
  };
};
